import React from "react";
import { Box, Paper, IconButton, Typography, Popover } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { isViewOnly } from "../utils/permissions";
import DeleteIcon from "@mui/icons-material/Delete";
import PlaceIcon from "@mui/icons-material/Place";
import { Ignition } from "../types/ignitionRegime";

interface IgnitionPopoverProps {
  ignition: Ignition | null;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  onDelete: (ignition: Ignition) => void;
}

const IgnitionPopover: React.FC<IgnitionPopoverProps> = ({
  ignition,
  anchorPosition,
  onClose,
  onDelete,
}) => {
  const open = Boolean(ignition && anchorPosition);
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);

  if (!ignition) return null;

  const getCoordinatesDisplay = () => {
    if (!ignition.geometry) return null;
    
    if (ignition.geometry.type === "Point") {
      const coords = ignition.geometry.coordinates as number[];
      return `${coords[1].toFixed(6)}, ${coords[0].toFixed(6)}`;
    }
    return null;
  };

  const coordinates = getCoordinatesDisplay();

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <Paper sx={{ p: 2, maxWidth: 300, bgcolor: "#212b37", color: "white" }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
          {ignition.properties.name || "Ignition Point"}
        </Typography>

        <Typography
          variant="body2"
          sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
        >
          <PlaceIcon sx={{ mr: 1, fontSize: 18 }} />
          Type: {ignition.properties.geom_type || "Point"}
        </Typography>

        {coordinates && (
          <Typography
            variant="body2"
            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
          >
            <PlaceIcon sx={{ mr: 1, fontSize: 18 }} />
            Coordinates: {coordinates}
          </Typography>
        )}

        {!viewOnly && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <IconButton
              size="small"
              onClick={() => onDelete(ignition)}
              sx={{ color: "#ff5252" }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Paper>
    </Popover>
  );
};

export default IgnitionPopover;
